import _ from 'lodash';
import urls from '../../../util/urls';
import consentActionTypes from './actionTypes';
import queryString from 'query-string';
import NotificationHelper from '../../util/NotificationHelper';
import contextKeys from './contextKeys';
import {
  asyncRequestContextsActions,
  asyncRequestContextsSelectors,
} from '../asyncRequestContexts';
import { notificationsActions } from '../notifications';
import { selectActiveProjectId } from '../projectPicker/selectors';

function getResponseContextProps(contextKey: string) {
  if (!contextKey) return null;

  return {
    contextKey,
    parseResponseItems: (data: any) => {
      // if single resource in response
      if (_.get(data, 'id')) {
        return data;
      } else {
        return _.get(data, 'items', []);
      }
    },
    parseNextToken: (data: any) => _.get(data, 'links.next'),
  };
}

function fetchConsentForms({
  params,
  next,
  contextKey,
}: {
  params?: any;
  next?: any;
  contextKey?: string;
}) {
  return async (dispatch: any) => {
    let nextUrl;
    if (next) {
      const parts = next.split('?');
      nextUrl = urls.api.consent.consentForms({
        ...queryString.parse(parts.pop()),
        ...params,
      });
    }
    await dispatch({
      type: consentActionTypes.FETCH_CONSENT_FORMS,
      url: nextUrl || urls.api.consent.consentForms(params),
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      responseContext: getResponseContextProps(contextKey),
    });
  };
}

function fetchProjectConsents({
  params,
  next,
  contextKey,
}: {
  params?: any;
  next?: any;
  contextKey?: string;
}) {
  return async (dispatch: any) => {
    let nextUrl;
    if (next) {
      const parts = next.split('?');
      nextUrl = urls.api.consent.projectConsents({
        ...queryString.parse(parts.pop()),
        ...params,
      });
    }
    await dispatch({
      type: consentActionTypes.FETCH_PROJECT_CONSENTS,
      url: nextUrl || urls.api.consent.projectConsents(params),
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      responseContext: getResponseContextProps(contextKey),
    });
  };
}

function fetchUserConsents({
  params,
  next,
  id,
  contextKey,
  notification = new NotificationHelper(),
}: {
  params?: any;
  next?: any;
  id?: string;
  contextKey?: string;
  notification?: NotificationHelper;
}) {
  return async (dispatch: any, getState: any) => {
    const projectId = selectActiveProjectId(getState());
    params = { projectId, ...params };
    let nextUrl;
    if (next) {
      const parts = next.split('?');
      nextUrl = urls.api.consent.userConsents({
        ...queryString.parse(parts.pop()),
        ...params,
      });
    }
    await dispatch({
      type: consentActionTypes.FETCH_USER_CONSENTS,
      url: nextUrl || urls.api.consent.userConsents(params, id),
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      responseContext: getResponseContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function updateUserConsent(
  directiveId: string,
  directive: any,
  contextKey: string,
  notification = new NotificationHelper(),
) {
  return async (dispatch: any) => {
    await dispatch({
      fetchOptions: {
        method: 'PATCH',
        body: JSON.stringify(directive),
      },
      type: consentActionTypes.UPDATE_USER_CONSENT,
      url: urls.api.consent.userConsents({}, directiveId),
      responseContext: getResponseContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function fetchPatientDirectives(
  patientId: string,
  contextKey: string,
  notification = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    await dispatch({
      type: consentActionTypes.FETCH_PATIENT_DIRECTIVES,
      url: urls.api.consent.projectConsents({
        patientId,
        projectId: selectActiveProjectId(getState()),
      }),
      responseContext: getResponseContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function deleteConsentDirective(
  directiveId: string,
  contextKey: string,
  notification = new NotificationHelper(),
) {
  return async (dispatch: any) => {
    await dispatch({
      type: consentActionTypes.DELETE_CONSENT_DIRECTIVE,
      url: urls.api.consent.consentDirective(directiveId),
      fetchOptions: {
        method: 'DELETE',
      },
      responseContext: getResponseContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function deleteProjectPatient(
  projectId: string,
  patientId: string,
  notification = new NotificationHelper(),
) {
  return async (dispatch: any) => {
    await dispatch({
      fetchOptions: {
        method: 'DELETE',
      },
      type: consentActionTypes.DELETE_PROJECT_PATIENT,
      url: urls.api.consent.patient(projectId, patientId),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function fetchQuestionnaire(
  id: string,
  account: string,
  project: string,
  contextKey: string,
  notification = new NotificationHelper(),
) {
  return async (dispatch: any) => {
    await dispatch({
      type: consentActionTypes.FETCH_CONSENT_QUESTIONNAIRE,
      url: urls.api.fhir.questionnaire({
        id,
        account,
        params: {
          _tag: `http://lifeomic.com/fhir/dataset|${project}`,
        },
      }),
      responseContext: getResponseContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function fetchQuestionnaireResponse(
  id: string,
  account: string,
  project: string,
  contextKey: string,
  notification = new NotificationHelper(),
) {
  return async (dispatch: any) => {
    await dispatch({
      type: consentActionTypes.FETCH_CONSENT_QUESTIONNAIRE_RESPONSE,
      url: urls.api.fhir.questionnaireResponse({
        id,
        account,
        params: {
          _tag: `http://lifeomic.com/fhir/dataset|${project}`,
        },
      }),
      responseContext: getResponseContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function resetConsentDetailState() {
  return {
    type: consentActionTypes.RESET_CONSENT_DETAIL_STATE,
  };
}

function setConsentDetailInitializeState(initialized: boolean) {
  return {
    type: consentActionTypes.SET_CONSENT_DETAIL_INITIALIZED_STATE,
    initialized,
  };
}

function setConsentDetailConsent(consent: any) {
  return {
    type: consentActionTypes.SET_CONSENT_DETAIL_CONSENT,
    consent,
  };
}

function setConsentDetailQuestionnaire(questionnaire: any) {
  return {
    type: consentActionTypes.SET_CONSENT_DETAIL_QUESTIONNAIRE,
    questionnaire,
  };
}

function setConsentDetailQuestionnaireResponse(questionnaireResponse: any) {
  return {
    type: consentActionTypes.SET_CONSENT_DETAIL_QUESTIONNAIRE_RESPONSE,
    questionnaireResponse,
  };
}

// verifies no errors occurred with the request and returns the singular response
const verifyAndExtractResponse = (state: any, contextKey: string) => {
  const { errorSelector, responsesSelector } =
    asyncRequestContextsSelectors.bindContextKeyToSelectors(contextKey);

  const requestError = errorSelector(state);
  if (requestError) throw new Error(requestError);

  return responsesSelector(state)[0];
};

function initializeConsentDetail(
  account: string,
  project: string,
  consent: any,
  _questionnaireId: string,
  questionnaireResponseId: string,
  activeProjectForm: any,
  messages = {} as any,
) {
  return async (dispatch: any, getState: any) => {
    dispatch(resetConsentDetailState());
    dispatch(setConsentDetailInitializeState(false));

    try {
      const {
        fetchConsentQuestionnaireResponse:
          fetchConsentQuestionnaireResponseContextKey,
      } = contextKeys;

      // reset the request state
      dispatch(
        asyncRequestContextsActions.resetContext(
          fetchConsentQuestionnaireResponseContextKey,
        ),
      );

      const safeDispatchErrorNotification = () => {
        if (messages.consentNotFound)
          dispatch(
            notificationsActions.addNotification(messages.consentNotFound, {
              statusType: 'error',
            }),
          );
      };

      if (!consent) {
        safeDispatchErrorNotification();
        return;
      }

      dispatch(setConsentDetailConsent(consent));

      const consentForm = consent.form || activeProjectForm;

      // questionnaire and response should both be found on the consent object
      if (!consentForm || !questionnaireResponseId) {
        safeDispatchErrorNotification();
        return;
      }

      // fetch the associated questionnaire & questionnaireResponses
      await dispatch(
        fetchQuestionnaireResponse(
          questionnaireResponseId,
          account,
          project,
          fetchConsentQuestionnaireResponseContextKey,
        ),
      );

      const questionnaireResponse = verifyAndExtractResponse(
        getState(),
        fetchConsentQuestionnaireResponseContextKey,
      );

      if (!questionnaireResponse) {
        safeDispatchErrorNotification();
        return;
      }

      dispatch(setConsentDetailQuestionnaire(consentForm));
      dispatch(setConsentDetailQuestionnaireResponse(questionnaireResponse));
    } catch (e) {
      console.error(e);
      if (messages.generalError)
        dispatch(
          notificationsActions.addNotification(messages.generalError, {
            statusType: 'error',
          }),
        );
    } finally {
      dispatch(setConsentDetailInitializeState(true));
    }
  };
}

export default {
  fetchProjectConsents,
  fetchConsentForms,
  fetchUserConsents,
  updateUserConsent,
  deleteConsentDirective,
  deleteProjectPatient,
  fetchQuestionnaire,
  fetchQuestionnaireResponse,
  initializeConsentDetail,
  setConsentDetailQuestionnaireResponse,
  resetConsentDetailState,
  fetchPatientDirectives,
};
